import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Section, SectionHeader } from "@/components/ui/Section";
import { motion } from "framer-motion";

export interface FAQ {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQ[];
  title?: string;
  subtitle?: string;
  className?: string;
  showSchema?: boolean;
  pageUrl?: string;
  /** If true, wraps in Section component with title. If false, just renders accordion */
  standalone?: boolean;
}

export function FAQSection({ 
  faqs, 
  title,
  subtitle,
  className = "", 
  showSchema = false, 
  pageUrl,
  standalone = true 
}: FAQSectionProps) {
  // Generate FAQ Schema for this section
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(pageUrl && { "url": pageUrl }),
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const accordionContent = (
    <>
      {showSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      )}
      
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.03 }}
            viewport={{ once: true }}
          >
            <AccordionItem value={`faq-${index}`} className="border-white/10">
              <AccordionTrigger className="text-left font-display font-semibold text-base md:text-lg text-white hover:text-accent transition-colors py-6">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-white/70 leading-[1.8] pb-6 text-base max-w-3xl">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          </motion.div>
        ))}
      </Accordion>
    </>
  );

  if (!standalone) {
    return <div className={className}>{accordionContent}</div>;
  }

  return (
    <Section className={className} background="muted">
      {title && <SectionHeader title={title} subtitle={subtitle} variant="dark" />}
      <div className="max-w-3xl mx-auto">
        {accordionContent}
      </div>
    </Section>
  );
}
